
import React from "react";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Skeleton } from "@/components/ui/skeleton";
import { TimePeriod } from "@/utils/dashboardUtils";

interface CO2ByType {
  rideType: string;
  totalCO2: number;
  totalDistance: number;
}

interface CO2ComparisonData {
  currentCO2?: number;
  previousCO2?: number;
  currentPeriodLabel?: string;
  previousPeriodLabel?: string;
}

interface CO2StatisticsProps {
  timeFilter: TimePeriod;
  onTimeFilterChange: (value: string) => void;
  co2ByType: CO2ByType[];
  co2Comparison: CO2ComparisonData;
  isLoading: boolean;
}

const CO2Statistics: React.FC<CO2StatisticsProps> = ({
  timeFilter,
  onTimeFilterChange,
  co2ByType,
  co2Comparison,
  isLoading,
}) => {
  // Sort the CO2 data by total CO2 amount for better visualization
  const sortedCO2Data = [...co2ByType].sort((a, b) => b.totalCO2 - a.totalCO2);

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle>CO₂ Offset by Ride Type</CardTitle>
            <div className="ml-auto">
              <ToggleGroup type="single" value={timeFilter} onValueChange={onTimeFilterChange}>
                <ToggleGroupItem value="day" aria-label="Filter by day">Day</ToggleGroupItem>
                <ToggleGroupItem value="week" aria-label="Filter by week">Week</ToggleGroupItem>
                <ToggleGroupItem value="month" aria-label="Filter by month">Month</ToggleGroupItem>
                <ToggleGroupItem value="year" aria-label="Filter by year">Year</ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-6 w-full" />
              ))}
            </div>
          ) : sortedCO2Data && sortedCO2Data.length ? (
            <div>
              {sortedCO2Data.map((v) => (
                <div key={v.rideType} className="flex items-center justify-between py-2 border-b last:border-b-0">
                  <span className="capitalize">{v.rideType}</span>
                  <span className="font-mono">
                    {v.totalCO2.toFixed(2)} kg
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-muted-foreground text-sm py-6 text-center">
              No data for this time period.
            </div>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle>
              CO₂ Offset Comparison
            </CardTitle>
            <div className="ml-auto">
              <ToggleGroup type="single" value={timeFilter} onValueChange={onTimeFilterChange}>
                <ToggleGroupItem value="day" aria-label="Compare by day">Day</ToggleGroupItem>
                <ToggleGroupItem value="week" aria-label="Compare by week">Week</ToggleGroupItem>
                <ToggleGroupItem value="month" aria-label="Compare by month">Month</ToggleGroupItem>
                <ToggleGroupItem value="year" aria-label="Compare by year">Year</ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div>
              <Skeleton className="h-8 w-2/3 mb-4" />
              <Skeleton className="h-6 w-1/2" />
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between">
                <span>{co2Comparison.currentPeriodLabel || "Current"}</span>
                <span className="font-mono">
                  {co2Comparison.currentCO2?.toFixed(2) ?? "0.00"} kg
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>{co2Comparison.previousPeriodLabel || "Previous"}</span>
                <span className="font-mono text-muted-foreground">
                  {co2Comparison.previousCO2?.toFixed(2) ?? "0.00"} kg
                </span>
              </div>
              {typeof co2Comparison.currentCO2 === "number" &&
               typeof co2Comparison.previousCO2 === "number" && (
                <div className="mt-4 text-sm">
                  Change: 
                  <span
                    className={
                      co2Comparison.currentCO2 - co2Comparison.previousCO2 >= 0
                        ? "text-green-600 ml-1"
                        : "text-red-600 ml-1"
                    }
                  >
                    {(
                      co2Comparison.currentCO2 -
                      co2Comparison.previousCO2
                    ).toFixed(2)} kg
                  </span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CO2Statistics;
